import { Link, useParams } from "react-router-dom";
import { OrderLink } from "@/components/EntityLink";
import { ArrowLeft } from "lucide-react";
import { readCustomer, type AdminCustomerFingerprint, type AdminOrderRow } from "@/api/admin";
import { useAsync } from "@/lib/useAsync";
import { money, when } from "@/lib/format";
import { statusBadge } from "@/screens/OrdersScreen";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { DataTable } from "@/components/ui/paged-list";
import { Kpi } from "@/components/ui/kpi";
import { Notice } from "@/components/ui/feedback";
import { PageHeader } from "@/components/ui/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import { TD, TH, TR } from "@/components/ui/table";

/**
 * One address, and everything bought with it.
 *
 * THE FINGERPRINT SECTION IS A DELIBERATE REVERSAL. `admin-orders.ts` refuses
 * to select `client_ip` and `user_agent`, on the grounds that no screen shows a
 * customer's address and device. The operator asked for exactly that, so it is
 * offered here -- on this screen only, never in a list, and never beside
 * `attempt_key`, which is bearer-grade and never leaves the worker.
 *
 * AN EMPTY FINGERPRINT LIST IS THE POLICY WORKING, not a fault. Those two
 * columns are the ones `cron/prune-pii.ts` clears once an order is older than
 * `pii_retention_days`, counted from when it was placed -- so an older order
 * shows nothing, and the screen says that rather than leaving a blank table
 * that reads as "we never had it".
 *
 * The order rows are `AdminOrderRow`, the same shape and the same status badge
 * the Orders screen uses, so one order cannot be described two ways.
 */
export function CustomerScreen() {
  const { email = "" } = useParams();
  const { data, loading, error } = useAsync(() => readCustomer(email), [email]);

  const customer = data?.customer;

  return (
    <div className="space-y-4">
      <PageHeader title={email} pretitle="Customer">
        <Button variant="outline" size="sm" onClick={() => history.back()}>
          <ArrowLeft aria-hidden className="h-3.5 w-3.5" />
          Back
        </Button>
      </PageHeader>

      {error && (
        <Notice tone="error">
          {error}{" "}
          <Link to="/customers" className="underline underline-offset-4">
            Back to the list
          </Link>
        </Notice>
      )}

      {loading && !data ? (
        <Card>
          <CardContent className="space-y-3">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </CardContent>
        </Card>
      ) : customer ? (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi label="Orders" value={customer.orders} />
            <Kpi
              label="Paid"
              value={customer.paid_orders}
              hint={
                customer.orders > customer.paid_orders
                  ? `${customer.orders - customer.paid_orders} never paid for`
                  : undefined
              }
            />
            <Kpi label="Spent" value={money(customer.spent_cents)} hint="Paid orders only" />
            <Kpi label="First order" value={when(customer.first_order_at)} />
          </div>

          <DataTable
            title="Orders"
            columns={6}
            rows={data.orders}
            loading={false}
            empty="No orders."
            head={
              <>
                <TH>Code</TH>
                <TH>Status</TH>
                <TH>Package</TH>
                <TH numeric>Qty</TH>
                <TH numeric>Total</TH>
                <TH>Placed</TH>
              </>
            }
            renderRow={(order: AdminOrderRow) => (
              <TR key={order.id}>
                <TD>
                  <OrderLink code={order.order_code} />
                </TD>
                <TD>{statusBadge(order.status)}</TD>
                <TD className="text-secondary">{order.package_id}</TD>
                <TD numeric>{order.quantity}</TD>
                <TD numeric>{money(order.total_cents)}</TD>
                <TD className="whitespace-nowrap text-muted-foreground">{when(order.created_at)}</TD>
              </TR>
            )}
          />
          {/* The worker bounds this read (`admin-customers.ts`, LIMIT 100)
              and the count above does not stop there.
              A table of 100 under "140" with nothing marking the cut would be
              the one thing on this screen that does not say what it cannot
              show. */}
          {customer.orders > data.orders.length && (
            <p className="text-xs text-muted-foreground">
              Showing the latest {data.orders.length} of {customer.orders} orders.
            </p>
          )}

          <DataTable
            title="Where the orders came from"
            columns={4}
            rows={data.fingerprints}
            loading={false}
            empty="Nothing kept. These are cleared once an order passes the retention window."
            head={
              <>
                <TH>Order</TH>
                <TH>Address</TH>
                <TH>Device</TH>
                <TH>Placed</TH>
              </>
            }
            renderRow={(print: AdminCustomerFingerprint) => (
              <TR key={print.order_code}>
                <TD className="font-mono text-xs">{print.order_code}</TD>
                <TD className="font-mono text-xs">{print.client_ip ?? "—"}</TD>
                <TD className="max-w-[360px] text-xs text-secondary">
                  {/* Truncated with the full string on hover: a user agent is
                      120 characters of which the interesting part is at the
                      end, and letting it set the column width pushes every
                      other column off a laptop screen. */}
                  <span className="block truncate" title={print.user_agent ?? undefined}>
                    {print.user_agent ?? "—"}
                  </span>
                </TD>
                <TD className="whitespace-nowrap text-muted-foreground">{when(print.created_at)}</TD>
              </TR>
            )}
          />
        </>
      ) : null}
    </div>
  );
}
